import type { Map as LeafletMap, ControlOptions } from 'leaflet';
import { Control, DomEvent, DomUtil } from 'leaflet';
import { ODMAP__BUTTON__ZOOM_RESET__TITEL } from '../../../texts';

type ZoomReset = {
  _createButton(
    html: Element,
    title: string,
    className: string,
    container: HTMLElement,
    fn: () => void,
  ): HTMLAnchorElement;
  _map: LeafletMap;
  _updateDisabled(): void;
  _zoomReset(): void;
  _zoomResetButton: HTMLAnchorElement;
  options: ControlOptions & {
    zoomResetContent: Element;
    zoomResetTitle: string;
  };

  // von Control:
  _refocusOnMap(e: Event): void;
};

type ZoomResetControl = ZoomReset & typeof Control;

export function resetZoom(map: LeafletMap) {
  if (map.getZoom() > map.getMinZoom()) {
    map.setZoom(map.getMinZoom());
  }
}

export const ZoomResetControl = Control.extend({
  options: {
    // eslint-disable-next-line i18next/no-literal-string
    position: 'bottomright',
    // eslint-disable-next-line i18next/no-literal-string
    zoomResetContent: (() => {
      const element = document.createElement('i');
      element.className = 'bi bi-arrows-fullscreen';
      return element;
    })(),
    zoomResetTitle: ODMAP__BUTTON__ZOOM_RESET__TITEL,
  },

  onAdd(this: ZoomResetControl, map: LeafletMap) {
    // eslint-disable-next-line i18next/no-literal-string
    const className = 'vwp-control-reset';
    const container = DomUtil.create('div', `${className} leaflet-bar`);
    const { options } = this;

    this._zoomResetButton = this._createButton(
      options.zoomResetContent,
      options.zoomResetTitle,
      className,
      container,
      this._zoomReset.bind(this),
    );

    this._updateDisabled();
    // eslint-disable-next-line @typescript-eslint/unbound-method,i18next/no-literal-string
    map.on('zoomend zoomlevelschange', this._updateDisabled, this);

    return container;
  },

  onRemove(map: LeafletMap) {
    // eslint-disable-next-line @typescript-eslint/unbound-method,i18next/no-literal-string
    map.off('zoomend zoomlevelschange', this._updateDisabled, this);
  },

  _zoomReset(this: ZoomResetControl) {
    resetZoom(this._map);
  },

  _createButton(
    this: ZoomResetControl,
    html: Element,
    title: string,
    className: string,
    container: HTMLElement,
    fn: () => void,
  ): HTMLAnchorElement {
    const link = DomUtil.create('a', className, container);
    link.replaceChildren(html);
    link.href = '#';
    link.title = title;

    /*
     * Will force screen readers like VoiceOver to read this as it is labeled
     */
    // eslint-disable-next-line i18next/no-literal-string
    link.setAttribute('role', 'button');
    // eslint-disable-next-line i18next/no-literal-string
    link.setAttribute('aria-label', title);

    DomEvent.disableClickPropagation(link);
    // eslint-disable-next-line i18next/no-literal-string
    DomEvent.on(link, 'click', DomEvent.stop);
    // eslint-disable-next-line i18next/no-literal-string
    DomEvent.on(link, 'click', fn, this);
    // eslint-disable-next-line @typescript-eslint/unbound-method,i18next/no-literal-string
    DomEvent.on(link, 'click', this._refocusOnMap, this);

    return link;
  },

  _updateDisabled(this: ZoomResetControl) {
    const map = this._map;
    // eslint-disable-next-line i18next/no-literal-string
    const className = 'leaflet-disabled';

    if (map.getZoom() === map.getMinZoom()) {
      if (this._zoomResetButton.getAttribute('aria-disabled') !== 'true') {
        this._zoomResetButton.classList.add(className);
        // eslint-disable-next-line i18next/no-literal-string
        this._zoomResetButton.setAttribute('aria-disabled', 'true');
      }
    } else {
      // es kann noch herausgezoomt werden
      // eslint-disable-next-line no-lonely-if
      if (this._zoomResetButton.getAttribute('aria-disabled') === 'true') {
        this._zoomResetButton.classList.remove(className);
        // eslint-disable-next-line i18next/no-literal-string
        this._zoomResetButton.setAttribute('aria-disabled', 'false');
      }
    }
  },
});
